import type { User } from 'oidc-client-ts';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Spinner } from '@crm/ui';
import { userManager } from './oidc.ts';

interface AuthState {
  user: User;
  accessToken: () => string;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth hors de AuthProvider');
  return ctx;
}

export function returnToFromState(state: unknown): string {
  const r = (state as { returnTo?: unknown } | undefined)?.returnTo;
  if (typeof r !== 'string' || !r.startsWith('/') || r.startsWith('//') || /[\u0000-\u001f\u007f\\]/u.test(r)) return '/';
  try {
    return new URL(r, window.location.origin).origin === window.location.origin ? r : '/';
  } catch {
    return '/';
  }
}

/**
 * Démarrage de la session, une seule fois par chargement de page (le mode strict de React
 * exécute les effets deux fois ; un code d'autorisation ne s'échange qu'une fois).
 * Renvoie null quand une redirection vers Keycloak est en cours.
 */
type SessionBootstrap = { user: User | null; returnTo?: string };
let bootstrap: Promise<SessionBootstrap> | null = null;
function startSession(): Promise<SessionBootstrap> {
  bootstrap ??= (async () => {
    if (window.location.pathname === '/callback') {
      const signedIn = await userManager.signinRedirectCallback();
      return { user: signedIn, returnTo: returnToFromState(signedIn.state) };
    }
    const existing = await userManager.getUser();
    if (existing && !existing.expired) return { user: existing };
    await userManager.signinRedirect({ state: { returnTo: window.location.pathname + window.location.search + window.location.hash } });
    return { user: null };
  })();
  return bootstrap;
}

/** Rien ne s'affiche tant que la personne n'est pas connectée : redirection vers Keycloak. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const userRef = useRef<User | null>(null);
  const sessionReady = useRef(false);
  userRef.current = user;

  useEffect(() => {
    let cancelled = false;
    startSession().then(
      (session) => {
        if (!cancelled && session.user) {
          if (session.returnTo) {
            const returnTo = session.returnTo;
            // Le callback OIDC ne doit jamais modifier l'historique hors du routeur :
            // celui-ci conserve son index et restaure aussi les liens profonds.
            delete session.returnTo;
            navigate(returnTo, { replace: true });
          }
          sessionReady.current = true;
          setUser(session.user);
        }
      },
      (e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      },
    );
    // Le client OIDC émet userLoaded pendant le callback, avant que l'adresse de
    // retour soit connue. Ne pas monter la route /callback avant sa restauration.
    const onLoaded = (u: User) => { if (sessionReady.current) setUser(u); };
    const onExpired = () => void userManager.signinRedirect({ state: { returnTo: window.location.pathname + window.location.search + window.location.hash } });
    userManager.events.addUserLoaded(onLoaded);
    userManager.events.addAccessTokenExpired(onExpired);
    return () => {
      cancelled = true;
      userManager.events.removeUserLoaded(onLoaded);
      userManager.events.removeAccessTokenExpired(onExpired);
    };
  }, []);

  const accessToken = useCallback(() => userRef.current?.access_token ?? '', []);
  const logout = useCallback(async () => {
    try {
      localStorage.removeItem('crm.tenant');
    } catch {
      /* rien */
    }
    await userManager.signoutRedirect({ id_token_hint: userRef.current?.id_token });
  }, []);

  const value = useMemo(() => (user ? { user, accessToken, logout } : null), [user, accessToken, logout]);

  if (error) {
    return (
      <div className="grid min-h-full place-items-center p-6">
        <div className="max-w-md rounded-xl border border-red-200 bg-white p-6 text-sm">
          <p className="font-semibold text-red-800">La connexion a échoué : {error}</p>
          <button className="mt-4 font-semibold text-brand underline" onClick={() => window.location.assign('/')}>
            {t('errors.retry')}
          </button>
        </div>
      </div>
    );
  }
  if (!value) {
    return (
      <div className="grid min-h-full place-items-center">
        <Spinner label={t('app.redirecting')} />
      </div>
    );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
